import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import type { ChangesPerDayPoint, TimeWindow } from '../types/domain';

export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => adminApi.dashboard.stats(),
    staleTime: 60_000,
  });
}

export function useRecentExecutions(orgId?: string) {
  return useQuery({
    queryKey: ['dashboard', 'executions', orgId],
    queryFn: () => adminApi.dashboard.executions({ org_id: orgId, limit: 10 }),
  });
}

export function useRecentChanges(orgId?: string) {
  return useQuery({
    queryKey: ['dashboard', 'changes', orgId],
    queryFn: () => adminApi.dashboard.changes({ org_id: orgId, limit: 10 }),
  });
}

export function usePaginatedExecutions(orgId?: string, page = 1, perPage = 20) {
  return useQuery({
    queryKey: ['executions', orgId, page, perPage],
    queryFn: () => adminApi.dashboard.executions({ org_id: orgId }),
    select: (data) => {
      const start = (page - 1) * perPage;
      return { data: data.slice(start, start + perPage), total: data.length };
    },
  });
}

export function usePaginatedChanges(orgId?: string, page = 1, perPage = 20) {
  return useQuery({
    queryKey: ['changes', orgId, page, perPage],
    queryFn: () => adminApi.dashboard.changes({ org_id: orgId }),
    select: (data) => {
      const start = (page - 1) * perPage;
      return { data: data.slice(start, start + perPage), total: data.length };
    },
  });
}

export function zeroFill(rows: ChangesPerDayPoint[], days: TimeWindow): ChangesPerDayPoint[] {
  const map = new Map(rows.map((r) => [r.day, r.count]));
  const out: ChangesPerDayPoint[] = [];
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(today.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    out.push({ day: key, count: map.get(key) ?? 0 });
  }
  return out;
}

export function useChangesPerDay(days: TimeWindow) {
  return useQuery({
    queryKey: ['admin', 'dashboard', 'changes-per-day', days] as const,
    queryFn: () => adminApi.dashboard.changesPerDay(days),
    select: (rows) => zeroFill(rows, days),
    staleTime: 60_000,
  });
}
